import { useState, useEffect, useCallback } from 'react'
import { operatorApi } from '@services/api'
import { Button } from '@components/UI/Button'
import type { OperatorSubmissionDetail } from '@/types'
import styles from './PraticaStatus.module.css'

interface PraticaStatusProps {
  submissionId: number
  onBack?: () => void
  onNewPratica?: () => void
}

export function PraticaStatus({ submissionId, onBack, onNewPratica }: PraticaStatusProps) {
  const [data, setData] = useState<OperatorSubmissionDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const result = await operatorApi.getStatus(submissionId)
      setData(result)
      setError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore caricamento stato'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [submissionId])

  useEffect(() => {
    fetchStatus()
    
    // Polling ogni 30 secondi per aggiornamenti
    const interval = setInterval(fetchStatus, 30000)
    return () => clearInterval(interval)
  }, [fetchStatus])

  const handleResend = async (contactType: 'email' | 'phone') => {
    setIsResending(true)
    setResendSuccess(false)
    
    try {
      await operatorApi.resendToClient(submissionId, contactType)
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 3000)
      fetchStatus()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore reinvio'
      setError(message)
    } finally {
      setIsResending(false)
    }
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <i className="bi bi-arrow-repeat" />
        <span>Caricamento stato pratica...</span>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className={styles.error}>
        <i className="bi bi-exclamation-triangle" />
        <span>{error}</span>
        <Button variant="secondary" onClick={fetchStatus}>
          Riprova
        </Button>
      </div>
    )
  }

  if (!data) return null

  const { submission, client, operator, signing, overall_status, fornitura } = data

  const formatTipoAttivazione = (tipo: string | null) => {
    if (!tipo) return null
    const labels: Record<string, string> = {
      'cambio_fornitore': 'Cambio Fornitore',
      'voltura_ordinaria': 'Voltura Ordinaria',
      'voltura_mortis_causa': 'Voltura Mortis Causa',
      'subentro': 'Subentro',
      'nuovo_allaccio': 'Nuovo Allaccio',
      'attivazione': 'Attivazione',
      'riattivazione': 'Riattivazione'
    }
    return labels[tipo] || tipo.replace(/_/g, ' ')
  }

  const formatAddress = (indirizzo: string | null, civico: string | null, cap: string | null, citta: string | null, provincia: string | null) => {
    const parts = []
    if (indirizzo) parts.push(indirizzo + (civico ? ' ' + civico : ''))
    if (cap || citta) parts.push([cap, citta].filter(Boolean).join(' '))
    if (provincia) parts.push('(' + provincia + ')')
    return parts.join(', ') || null
  }

  // Contenuto informativo (usato sia in layout singolo che split)
  const infoCards = (
    <>
      {/* Tipo Cliente */}
      {submission.tipo_cliente && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className={`bi ${submission.tipo_cliente === 'business' ? 'bi-building' : 'bi-house-door'}`} />
            <h2>Tipo Cliente</h2>
            <span
              style={{
                marginLeft: 'auto',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                background: submission.tipo_cliente === 'business' ? '#dbeafe' : '#dcfce7',
                color: submission.tipo_cliente === 'business' ? '#1d4ed8' : '#166534',
              }}
            >
              {submission.tipo_cliente === 'business' ? 'Business' : 'Domestico'}
            </span>
          </div>
        </div>
      )}

      {/* Client Info */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <i className="bi bi-person" />
          <h2>Dati Cliente</h2>
        </div>
        <div className={styles.cardGrid}>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Nome</span>
            <span className={styles.cardValue}>{client.nome} {client.cognome}</span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Codice Fiscale</span>
            <span className={styles.cardValue}>{client.codice_fiscale}</span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Email</span>
            <span className={styles.cardValue}>{client.email}</span>
          </div>
          <div className={styles.cardItem}>
            <span className={styles.cardLabel}>Telefono</span>
            <span className={styles.cardValue}>{client.telefono}</span>
          </div>
        </div>
      </div>

      {/* Dettagli Fornitura */}
      {fornitura && (fornitura.ha_luce || fornitura.ha_gas) && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-lightning-charge" />
            <h2>Dettagli Fornitura</h2>
          </div>
          
          {fornitura.ha_luce && (
            <div className={styles.fornituraBlock}>
              <div className={styles.fornituraTitle}>
                <span className={styles.fornituraBadgeLuce}>
                  <i className="bi bi-lightning-charge-fill" />
                  Luce
                </span>
                {fornitura.tipo_attivazione_luce && (
                  <span className={styles.fornituraAttivazione}>
                    {formatTipoAttivazione(fornitura.tipo_attivazione_luce)}
                  </span>
                )}
              </div>
              <div className={styles.cardGrid}>
                {fornitura.pod_luce && (
                  <div className={styles.cardItem}>
                    <span className={styles.cardLabel}>POD</span>
                    <span className={`${styles.cardValue} ${styles.monoValue}`}>{fornitura.pod_luce}</span>
                  </div>
                )}
                {fornitura.codice_prodotto_luce && (
                  <div className={styles.cardItem}>
                    <span className={styles.cardLabel}>Prodotto</span>
                    <span className={styles.cardValue}>{fornitura.codice_prodotto_luce}</span>
                  </div>
                )}
                {formatAddress(fornitura.indirizzo_fornitura_luce, fornitura.civico_fornitura_luce, fornitura.cap_fornitura_luce, fornitura.citta_fornitura_luce, fornitura.provincia_fornitura_luce) && (
                  <div className={styles.cardItem} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.cardLabel}>Indirizzo Fornitura</span>
                    <span className={styles.cardValue}>
                      {formatAddress(fornitura.indirizzo_fornitura_luce, fornitura.civico_fornitura_luce, fornitura.cap_fornitura_luce, fornitura.citta_fornitura_luce, fornitura.provincia_fornitura_luce)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {fornitura.ha_gas && (
            <div className={styles.fornituraBlock}>
              <div className={styles.fornituraTitle}>
                <span className={styles.fornituraBadgeGas}>
                  <i className="bi bi-fire" />
                  Gas
                </span>
                {fornitura.tipo_attivazione_gas && (
                  <span className={styles.fornituraAttivazione}>
                    {formatTipoAttivazione(fornitura.tipo_attivazione_gas)}
                  </span>
                )}
              </div>
              <div className={styles.cardGrid}>
                {fornitura.pdr_gas && (
                  <div className={styles.cardItem}>
                    <span className={styles.cardLabel}>PDR</span>
                    <span className={`${styles.cardValue} ${styles.monoValue}`}>{fornitura.pdr_gas}</span>
                  </div>
                )}
                {fornitura.codice_prodotto_gas && (
                  <div className={styles.cardItem}>
                    <span className={styles.cardLabel}>Prodotto</span>
                    <span className={styles.cardValue}>{fornitura.codice_prodotto_gas}</span>
                  </div>
                )}
                {formatAddress(fornitura.indirizzo_fornitura_gas, fornitura.civico_fornitura_gas, fornitura.cap_fornitura_gas, fornitura.citta_fornitura_gas, fornitura.provincia_fornitura_gas) && (
                  <div className={styles.cardItem} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.cardLabel}>Indirizzo Fornitura</span>
                    <span className={styles.cardValue}>
                      {formatAddress(fornitura.indirizzo_fornitura_gas, fornitura.civico_fornitura_gas, fornitura.cap_fornitura_gas, fornitura.citta_fornitura_gas, fornitura.provincia_fornitura_gas)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Operatore */}
      {submission.operator_username && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-headset" />
            <h2>Operatore</h2>
          </div>
          <div className={styles.cardGrid}>
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>Username</span>
              <span className={styles.cardValue}>{submission.operator_username}</span>
            </div>
          </div>
        </div>
      )}

      {/* Invio al cliente: cronologia senza URL (il link non è esposto agli operatori) */}
      {operator.link_sent_at && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-send-check" />
            <h2>Invio al cliente</h2>
          </div>
          <p className={styles.helperMuted}>
            Il cliente riceve il link di firma solo su email o SMS. Non è visibile in portale per evitare firme non autenticate.
          </p>

          <div className={styles.timeline}>
            <div className={`${styles.timelineItem} ${operator.link_sent_at ? styles.timelineItemDone : ''}`}>
              <div className={styles.timelineIcon}>
                <i className="bi bi-send" />
              </div>
              <div className={styles.timelineContent}>
                <span className={styles.timelineLabel}>Inviato</span>
                <span className={styles.timelineDate}>{formatDate(operator.link_sent_at)}</span>
              </div>
            </div>
            <div className={`${styles.timelineItem} ${operator.link_opened_at ? styles.timelineItemDone : ''}`}>
              <div className={styles.timelineIcon}>
                <i className="bi bi-eye" />
              </div>
              <div className={styles.timelineContent}>
                <span className={styles.timelineLabel}>Aperto dal cliente</span>
                <span className={styles.timelineDate}>{formatDate(operator.link_opened_at)}</span>
              </div>
            </div>
            <div className={`${styles.timelineItem} ${signing.completed_at ? styles.timelineItemDone : ''}`}>
              <div className={styles.timelineIcon}>
                <i className="bi bi-pen" />
              </div>
              <div className={styles.timelineContent}>
                <span className={styles.timelineLabel}>Firmato</span>
                <span className={styles.timelineDate}>{formatDate(signing.completed_at)}</span>
              </div>
            </div>
          </div>

          {overall_status.status !== 'completed' && (
            <div className={styles.resendSection}>
              {resendSuccess && (
                <div className={styles.resendSuccess}>
                  <i className="bi bi-check-circle" />
                  Invito reinviato con successo
                </div>
              )}
              <div className={styles.resendButtons}>
                <Button
                  variant="outline"
                  size="sm"
                  icon="envelope"
                  onClick={() => handleResend('email')}
                  loading={isResending}
                  disabled={!client.email}
                >
                  Reinvia Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  icon="phone"
                  onClick={() => handleResend('phone')}
                  loading={isResending}
                  disabled={!client.telefono}
                >
                  Reinvia SMS
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Signing Info */}
      {signing.session_id && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-pen" />
            <h2>Processo Firma</h2>
          </div>
          <div className={styles.cardGrid}>
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>Stato Sessione</span>
              <span className={styles.cardValue}>
                {signing.session_status === 'completed' && 'Completata'}
                {signing.session_status === 'signing' && 'In firma'}
                {signing.session_status === 'verified' && 'Verificato'}
                {signing.session_status === 'otp_sent' && 'OTP inviato'}
                {signing.session_status === 'pending' && 'In attesa'}
              </span>
            </div>
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>Firme Apposte</span>
              <span className={styles.cardValue}>{signing.signatures_count}</span>
            </div>
            {signing.signed_pdf_available && (
              <div className={styles.cardItem}>
                <span className={styles.cardLabel}>PDF Firmato</span>
                <span className={styles.cardValue}>
                  <i className="bi bi-file-earmark-pdf" style={{ color: '#dc2626', marginRight: '0.5rem' }} />
                  Disponibile
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SiDial Order */}
      {overall_status.status === 'completed' && submission.sidial_order_id && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-box-seam" />
            <h2>Ordine SiDial</h2>
          </div>
          <div className={styles.cardGrid}>
            <div className={styles.cardItem}>
              <span className={styles.cardLabel}>ID Ordine</span>
              <span className={styles.cardValue}>#{submission.sidial_order_id}</span>
            </div>
          </div>
        </div>
      )}

      {/* Operator Notes */}
      {operator.notes && (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <i className="bi bi-sticky" />
            <h2>Note Operative</h2>
          </div>
          <p className={styles.notes}>{operator.notes}</p>
        </div>
      )}
    </>
  )

  const hasPdf = signing.signed_pdf_available

  return (
    <div className={hasPdf ? styles.containerWide : styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1>Stato Pratica</h1>
          <span className={styles.submissionId}>#{submission.uuid.slice(0, 8)}</span>
        </div>
        <div className={styles.headerActions}>
          {onNewPratica && (
            <Button variant="secondary" icon="plus" onClick={onNewPratica}>
              Nuova Pratica
            </Button>
          )}
          {onBack && (
            <Button variant="ghost" icon="arrow-left" onClick={onBack}>
              Indietro
            </Button>
          )}
        </div>
      </div>

      {/* Progress Status */}
      <div className={styles.progressCard}>
        <div className={styles.progressHeader}>
          <div className={`${styles.statusBadge} ${styles[`status${overall_status.status.charAt(0).toUpperCase() + overall_status.status.slice(1)}`]}`}>
            {overall_status.status === 'completed' && <i className="bi bi-check-circle-fill" />}
            {overall_status.status === 'signing' && <i className="bi bi-pen-fill" />}
            {overall_status.status === 'opened' && <i className="bi bi-eye-fill" />}
            {overall_status.status === 'sent' && <i className="bi bi-send-fill" />}
            {overall_status.status === 'draft' && <i className="bi bi-file-earmark" />}
            {overall_status.label}
          </div>
          <span className={styles.progressPercent}>{overall_status.progress}%</span>
        </div>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill} 
            style={{ width: `${overall_status.progress}%` }}
          />
        </div>
        <p className={styles.progressDescription}>{overall_status.description}</p>
      </div>

      {/* Layout condizionale: split con PDF o colonna singola */}
      {hasPdf ? (
        <div className={styles.splitLayout}>
          <div className={styles.infoColumn}>
            {infoCards}
          </div>
          <div className={styles.pdfColumn}>
            <div className={styles.pdfSticky}>
              <div className={styles.pdfStickyHeader}>
                <i className="bi bi-file-earmark-pdf" />
                <h2>Contratto Firmato</h2>
              </div>
              <iframe
                src={`/backend/api/v1/download-signed/${signing.session_id}?view=inline`}
                title="Anteprima Contratto Firmato"
              />
            </div>
          </div>
        </div>
      ) : (
        infoCards
      )}

      {/* Refresh Button */}
      <div className={styles.footer}>
        <Button
          variant="ghost"
          icon="arrow-repeat"
          onClick={fetchStatus}
        >
          Aggiorna Stato
        </Button>
        <span className={styles.autoRefresh}>
          Aggiornamento automatico ogni 30s
        </span>
      </div>
    </div>
  )
}
